import { motion } from "framer-motion";

/* The Lexafide Operations Maturity Index — Gen1 → Gen5.
   A published-framework treatment: bold serial numbers, dense per-gen ops stats,
   a horizontal maturity axis, and a consultant-style diagnosis CTA below. */

const GENS = [
    {
        id: "g1",
        gen: "01",
        title: "The Solo Grinder",
        size: "1–2 attorneys",
        stats: [
            ["Inbound / mo",   "~80"],
            ["Ops headcount",  "0"],
            ["Systems",        "—"],
        ],
        pain: { label: "Pain", value: "Phone barely answered" },
        fit: "Not a fit yet",
        tone: "muted",
        height: 320,
    },
    {
        id: "g2",
        gen: "02",
        title: "The Growing Practice",
        size: "3–6 attorneys",
        stats: [
            ["Inbound / mo",     "~400"],
            ["Missed calls",     "22%"],
            ["Intake conv.",     "8–11%"],
        ],
        pain: { label: "Pain", value: "Manual intake, slipping quality" },
        fit: "Core buyer",
        tone: "primary",
        height: 360,
    },
    {
        id: "g3",
        gen: "03",
        title: "The Regional Player",
        size: "6–15 attorneys",
        stats: [
            ["Inbound / mo",     "~1,200"],
            ["Ops staff",        "3–4"],
            ["Intake conv.",     "12–14%"],
        ],
        pain: { label: "Pain", value: "Headcount fixing system problems" },
        fit: "Strong buyer",
        tone: "primary",
        height: 400,
    },
    {
        id: "g4",
        gen: "04",
        title: "The Scaled Firm",
        size: "15–50 attorneys",
        stats: [
            ["Inbound / mo",     "~3,800"],
            ["Ops team",         "8–12"],
            ["AI vendors",       "5+"],
        ],
        pain: { label: "Pain", value: "No orchestration between tools" },
        fit: "Connective tissue",
        tone: "primary",
        height: 440,
    },
    {
        id: "g5",
        gen: "05",
        title: "The Institution",
        size: "1,200+ intake agents",
        stats: [
            ["Calls / day",      "7,000"],
            ["Intake conv.",     "95.6%"],
            ["Built over",       "8 yrs"],
        ],
        pain: { label: "Edge", value: "Tech company that practices law" },
        fit: "The benchmark",
        tone: "institution",
        height: 490,
    },
];

const TONES = {
    muted: {
        cardBg: "var(--paper)",
        cardBorder: "1px dashed var(--border-strong)",
        genColor: "var(--muted)",
        title: "var(--ink-soft)",
        statsBg: "var(--cream-2)",
        statsLabel: "var(--muted)",
        statsValue: "var(--ink-soft)",
        painLabel: "var(--muted)",
        painValue: "var(--ink-soft)",
        fitBg: "rgba(35,31,35,0.05)",
        fitColor: "var(--ink-soft)",
        fitBorder: "var(--border-strong)",
        fitDot: "var(--muted)",
        accentLine: "var(--border-strong)",
    },
    primary: {
        cardBg: "var(--paper)",
        cardBorder: "1.5px solid var(--blue)",
        genColor: "var(--blue)",
        title: "var(--ink)",
        statsBg: "rgba(30,91,255,0.05)",
        statsLabel: "var(--ink-dim)",
        statsValue: "var(--ink)",
        painLabel: "var(--blue-deep)",
        painValue: "var(--ink)",
        fitBg: "var(--blue)",
        fitColor: "#FFFFFF",
        fitBorder: "var(--blue)",
        fitDot: "#FFFFFF",
        accentLine: "var(--blue)",
    },
    institution: {
        cardBg: "var(--ink)",
        cardBorder: "1.5px solid rgba(252,211,77,0.45)",
        genColor: "#FCD34D",
        title: "#FFFFFF",
        statsBg: "rgba(255,255,255,0.05)",
        statsLabel: "rgba(255,255,255,0.45)",
        statsValue: "#FFFFFF",
        painLabel: "#FCD34D",
        painValue: "rgba(255,255,255,0.92)",
        fitBg: "#FCD34D",
        fitColor: "var(--ink)",
        fitBorder: "#FCD34D",
        fitDot: "var(--ink)",
        accentLine: "#FCD34D",
    },
};

const GenColumn = ({ data, index }) => {
    const t = TONES[data.tone];
    const isInstitution = data.tone === "institution";
    const isMuted = data.tone === "muted";

    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.55, delay: index * 0.08, ease: [0.16, 1, 0.3, 1] }}
            data-testid={`firm-evolution-${data.id}`}
            className="relative flex flex-col rounded-2xl p-5 md:p-6 transition-all duration-300 hover:-translate-y-1"
            style={{
                background: t.cardBg,
                border: t.cardBorder,
                color: t.title,
                minHeight: data.height,
                boxShadow: isInstitution
                    ? "0 32px 80px -36px rgba(252,211,77,0.32), 0 16px 40px -18px rgba(35,31,35,0.6)"
                    : data.tone === "primary"
                        ? "0 24px 50px -28px rgba(30,91,255,0.28)"
                        : "none",
            }}
        >
            {/* Top: huge Gen number with a thin accent rule beneath */}
            <div>
                <div
                    className="font-display font-bold tabular-nums leading-none tracking-tight"
                    style={{
                        color: t.genColor,
                        fontSize: "clamp(2.4rem, 4vw, 3.4rem)",
                        opacity: isMuted ? 0.55 : 1,
                    }}
                >
                    {data.gen}
                    {isInstitution && (
                        <i className="fa-solid fa-star text-[#FCD34D] text-[14px] align-top ml-1.5" style={{ verticalAlign: "super" }} />
                    )}
                </div>
                <div
                    className="mt-2 h-px"
                    style={{ background: t.accentLine, opacity: isMuted ? 0.5 : 0.7 }}
                />
            </div>

            {/* Title + size */}
            <div className="mt-4">
                <h3
                    className="font-display font-semibold leading-[1.1] text-[16px] md:text-[18px]"
                    style={{ color: t.title }}
                >
                    {data.title}
                </h3>
                <p
                    className="font-mono text-[10.5px] tracking-[0.16em] uppercase mt-2"
                    style={{ color: isInstitution ? "rgba(255,255,255,0.55)" : "var(--muted)" }}
                >
                    {data.size}
                </p>
            </div>

            {/* Operational stats */}
            <div
                className="mt-5 rounded-xl px-3 py-3 flex flex-col gap-1.5"
                style={{ background: t.statsBg }}
            >
                {data.stats.map(([label, value]) => (
                    <div key={label} className="flex items-baseline justify-between gap-2">
                        <span
                            className="font-mono text-[9.5px] tracking-[0.14em] uppercase truncate"
                            style={{ color: t.statsLabel }}
                        >
                            {label}
                        </span>
                        <span
                            className="font-display font-semibold tabular-nums text-[14px] whitespace-nowrap"
                            style={{ color: t.statsValue }}
                        >
                            {value}
                        </span>
                    </div>
                ))}
            </div>

            {/* Pain / Edge tag */}
            <div className="mt-4">
                <div
                    className="font-mono text-[9px] tracking-[0.2em] uppercase font-semibold mb-1.5"
                    style={{ color: t.painLabel }}
                >
                    {data.pain.label}
                </div>
                <p
                    className="text-[12.5px] leading-snug"
                    style={{ color: t.painValue }}
                >
                    {data.pain.value}
                </p>
            </div>

            {/* Fit pill — sticks to bottom */}
            <div className="mt-auto pt-5">
                <span
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-mono text-[9.5px] tracking-[0.14em] uppercase font-semibold"
                    style={{
                        background: t.fitBg,
                        color: t.fitColor,
                        border: `1px solid ${t.fitBorder}`,
                    }}
                >
                    <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: t.fitDot }} />
                    {data.fit}
                </span>
            </div>
        </motion.div>
    );
};

const FirmEvolution = ({ compact = false }) => {
    return (
        <section
            data-testid="firm-evolution"
            className={`relative ${compact ? "py-16 md:py-20 bg-[var(--cream-2)] border-y border-[var(--border)]" : "py-20 md:py-28 bg-[var(--paper)]"}`}
        >
            <div className="max-w-[1280px] mx-auto px-6 md:px-10">
                {/* Header — published-framework treatment */}
                <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.6 }}
                    className="mb-10 md:mb-14 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 lg:items-end"
                >
                    <div className="lg:col-span-8">
                        <div className="flex items-center gap-3 mb-5">
                            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--ink)] text-[var(--paper)] font-mono text-[10px] tracking-[0.22em] uppercase font-semibold">
                                <i className="fa-solid fa-square-poll-vertical text-[10px]" />
                                A Lexafide framework
                            </span>
                            <span className="hidden md:inline-block h-px w-12 bg-[var(--border-strong)]" />
                            <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[var(--muted)]">
                                Ops Maturity Index · v1.0
                            </span>
                        </div>

                        {compact ? (
                            <h2 className="font-display text-[clamp(2rem,4.4vw,3.4rem)] leading-[1.02] tracking-tight font-semibold text-[var(--ink)]">
                                We&apos;ve mapped law firms{" "}
                                <span className="accent-blue">into five generations.</span>
                            </h2>
                        ) : (
                            <h2 className="font-display text-[clamp(2.1rem,4.6vw,3.6rem)] leading-[1.02] tracking-tight font-semibold text-[var(--ink)]">
                                Most firms can&apos;t name the gen they&apos;re in.{" "}
                                <span className="accent-blue">We&apos;ve classified all of them.</span>
                            </h2>
                        )}
                    </div>

                    {/* Authority numbers */}
                    <div className="lg:col-span-4">
                        <p className="text-[14.5px] text-[var(--ink-soft)] leading-relaxed mb-5">
                            {compact
                                ? "A 4-attorney shop and a 1,200-agent institution have nothing in common except needing the same outcomes. Lexafide handcrafts the deployment for each."
                                : "After living inside law firm operations for two decades — from solo grinders to institutions — we've mapped how legal operations actually evolve. Here's the index most attorneys haven't seen."}
                        </p>
                        <div className="grid grid-cols-3 gap-3 pt-5 border-t border-[var(--border)]">
                            {[
                                { v: "Live", l: "Agents in production today" },
                                { v: "12 mo", l: "Field data" },
                                { v: "5",     l: "Gens mapped" },
                            ].map((m) => (
                                <div key={m.l}>
                                    <div className="font-display font-bold text-[18px] md:text-[20px] text-[var(--ink)] tabular-nums leading-none">
                                        {m.v}
                                    </div>
                                    <div className="font-mono text-[9px] tracking-[0.16em] uppercase text-[var(--muted)] mt-1.5">
                                        {m.l}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </motion.div>

                {/* Skyline */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4 items-end">
                    {GENS.map((g, i) => (
                        <GenColumn key={g.id} data={g} index={i} />
                    ))}
                </div>

                {/* Maturity axis */}
                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    className="hidden lg:flex items-center gap-3 mt-6 px-2"
                    aria-hidden
                >
                    <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[var(--muted)] flex-shrink-0">
                        Ops maturity
                    </span>
                    <span className="flex-1 h-px bg-gradient-to-r from-[var(--border-strong)] via-[var(--blue)] to-[#FCD34D]" />
                    <i className="fa-solid fa-arrow-right text-[10px] text-[#FCD34D]" />
                </motion.div>

                {/* Closing CTA — consultant's diagnostic offer */}
                {!compact && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.65, delay: 0.2 }}
                        className="relative mt-14 md:mt-20 rounded-2xl bg-[var(--ink)] text-[var(--paper)] overflow-hidden"
                        data-testid="firm-evolution-frame"
                    >
                        {/* glow */}
                        <div
                            aria-hidden
                            className="absolute -top-32 -right-32 w-[440px] h-[440px] rounded-full pointer-events-none"
                            style={{ background: "radial-gradient(circle, rgba(30,91,255,0.32) 0%, rgba(30,91,255,0) 60%)" }}
                        />
                        <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 p-8 md:p-12 items-center">
                            <div className="lg:col-span-7">
                                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/15 text-white/85 font-mono text-[10px] tracking-[0.22em] uppercase font-semibold">
                                    <i className="fa-solid fa-stethoscope text-[10px] accent-blue" />
                                    The Lexafide diagnosis
                                </span>
                                <h3 className="font-display text-[clamp(1.5rem,2.8vw,2.3rem)] leading-[1.1] tracking-tight font-semibold mt-5">
                                    We&apos;ll tell you what gen you&apos;re at —{" "}
                                    <span className="accent-blue">and the three things to fix first.</span>
                                </h3>
                                <p className="mt-5 text-[15px] text-white/72 leading-relaxed">
                                    Every law firm in America is competing with Gen 5 firms in their city. We help you
                                    operate like one — without the 8 years and 1,200 people it took Morgan &amp; Morgan
                                    to get there.
                                </p>
                                <p className="mt-3 text-[13.5px] text-white/55 leading-relaxed italic">
                                    Gen 1? We&apos;ll tell you straight — not yet. Gen 2 and above? You&apos;re already
                                    feeling the gap. That&apos;s exactly what we&apos;re built for.
                                </p>
                            </div>

                            <div className="lg:col-span-5 lg:border-l lg:border-white/10 lg:pl-10">
                                <ul className="space-y-3 mb-7">
                                    {[
                                        ["fa-magnifying-glass-chart", "30-min ops audit, no slide deck"],
                                        ["fa-diagram-project",        "Custom workflow map of your firm"],
                                        ["fa-list-check",             "Three highest-leverage fixes ranked"],
                                    ].map(([icon, label]) => (
                                        <li key={label} className="flex items-start gap-3 text-[14px] text-white/82">
                                            <span className="w-7 h-7 rounded-md bg-[rgba(30,91,255,0.16)] accent-blue flex items-center justify-center flex-shrink-0 mt-0.5">
                                                <i className={`fa-solid ${icon} text-[11px]`} />
                                            </span>
                                            <span className="leading-snug">{label}</span>
                                        </li>
                                    ))}
                                </ul>
                                <a
                                    href="https://calendly.com/d/d3nd-zhb-2fr/lexafide-demo-discussion-30-minute-meeting-web"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="firm-evolution-cta"
                                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[var(--blue)] text-white font-medium text-sm shadow-[0_18px_36px_-14px_rgba(30,91,255,0.55)] hover:bg-white hover:text-[var(--ink)] transition-colors duration-300"
                                >
                                    Book your ops audit
                                    <i className="fa-solid fa-arrow-right text-xs" />
                                </a>
                            </div>
                        </div>
                    </motion.div>
                )}

                {/* Compact-mode footer strip */}
                {compact && (
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.55, delay: 0.2 }}
                        className="mt-10 md:mt-14 flex flex-wrap items-center justify-center gap-3 md:gap-5 text-center"
                    >
                        <span className="font-mono text-[10.5px] tracking-[0.18em] uppercase text-[var(--muted)]">
                            Different ops needs
                        </span>
                        <span className="hidden md:inline-block w-8 h-px bg-[var(--border-strong)]" />
                        <span className="font-display text-[15px] md:text-[16px] text-[var(--ink)] font-medium">
                            One Lexafide layer · handcrafted per firm
                        </span>
                        <span className="hidden md:inline-block w-8 h-px bg-[var(--border-strong)]" />
                        <a
                            href="/contact"
                            className="inline-flex items-center gap-1.5 font-mono text-[10.5px] tracking-[0.18em] uppercase accent-blue font-semibold hover:underline"
                            data-testid="firm-evolution-cta"
                        >
                            Map yours with us
                            <i className="fa-solid fa-arrow-right text-[9px]" />
                        </a>
                    </motion.div>
                )}
            </div>
        </section>
    );
};

export default FirmEvolution;
